import { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonPrimary } from '@comeon/ui';
import { selectLocale, selectBalance } from '~/components/account/ducks/index.js';
import Icon, { ICON_HEART } from '~/components/icon/index.js';
import { HeadWrapper, MainTitle, SubTitle } from '../../../misc/section-head.js';
import BinaryQuestion from '../../../binary-question.js';
import useTranslations from '../../../translation/use-translations';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import { spectatorTip } from '../ducks/we-spin-duck.js';
import { selectStreamView } from '../ducks/we-spin-selectors.js';
import { WE_SPIN_TIPPING_ROOM_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import useIsMounted from '~/hooks/use-is-mounted';

const TIP_IN_AMOUNTS = [10, 50, 100]; // Fetch as part of stream configuration?

const WeSpinGameRoomTip = ({ streamRef }) => {
	const isMounted = useIsMounted();

	const walletBalance = useSelector(selectBalance) || 0;
	const [tipAmount, setTipAmount] = useState(undefined);
	const [tipPopUp, setTipPopUp] = useState(false);
	const [firstTipPage, setFirstTipPage] = useState(true);

	const locale = useSelector(selectLocale);
	const streamView = useSelector(selectStreamView(streamRef));
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_TIPPING_ROOM_TEXT_KEYS);
	const { streamerRef, currencyCode } = streamView;

	const tipInAmountFormatted = useMemo(
		() =>
			TIP_IN_AMOUNTS.map(tipInAmount => ({
				tipInAmount,
				label: currencyFormatter.format(tipInAmount, currencyCode, locale)
			})),
		[currencyCode, locale]
	);
	const updateTipAmount = evt => {
		const value = Math.min(walletBalance, evt.target.value);
		setTipAmount(value);
	};

	const showPopup = useCallback(() => {
		setTipPopUp(true);
	}, [setTipPopUp]);
	const hidePopup = useCallback(() => {
		setTipPopUp(false);
	}, [setTipPopUp]);

	const dispatch = useDispatch();
	const onConfirm = useCallback(() => {
		hidePopup();
		setFirstTipPage(false);
		dispatch(spectatorTip(streamRef, tipAmount)).catch(() => {
			if (isMounted.current) {
				setFirstTipPage(true);
			}
		});
	}, [dispatch, hidePopup, isMounted, streamRef, tipAmount]);

	if (isLoadingTranslations) return null;
	return (
		<div className="u-p1 u-flex-available-height-column">
			{firstTipPage && (
				<>
					<BinaryQuestion
						shouldOpenBinary={tipPopUp}
						title={tr.WE_SPIN_TIPPING_ROOM_TIP_CONFIRM_HEADER}
						text={tr.WE_SPIN_TIPPING_ROOM_TIP_CONFIRM_TEXT}
						yesText={tr.WE_SPIN_TIPPING_ROOM_TIP_CONFIRM_BUTTON}
						onYesClick={onConfirm}
						noText={tr.WE_SPIN_TIPPING_ROOM_TIP_CANCEL_BUTTON}
						renderInParent
						onNoClick={hidePopup}
					>
						<div className="u-pt2">
							<table className="table--binary-question">
								<tbody>
									<tr>
										<td>{tr.WE_SPIN_TIPPING_ROOM_TIP_YOUR_TIP_LABEL}</td>
										<td>{tipAmount}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</BinaryQuestion>

					<HeadWrapper>
						<Icon type={ICON_HEART} />
						<MainTitle>{`${tr.WE_SPIN_TIPPING_ROOM_TIP_HEADER_TITLE} ${streamerRef}`}</MainTitle>
						<SubTitle>{tr.WE_SPIN_TIPPING_ROOM_TIP_HEADER_TEXT}</SubTitle>
					</HeadWrapper>

					<div className="form">
						<fieldset className="u-relative u-oh">
							<input
								type="number"
								id="tip-streamer-input"
								required
								className="input-amount u-text-c"
								onChange={updateTipAmount}
								value={tipAmount || ''}
							/>
							<label htmlFor="tip-streamer-input">{tr.WE_SPIN_TIPPING_ROOM_TIP_PLACEHOLDER}</label>
							<div className="form__border" />
						</fieldset>
					</div>
					<div className="u-flex u-pt1 u-pl1 u-pr1 u-pb-1-btn buy-in-buttons">
						{tipInAmountFormatted.map(({ tipInAmount, label }) => (
							<button
								value={tipInAmount}
								onClick={updateTipAmount}
								key={tipInAmount}
								className="btn btn--small"
							>
								{label}
							</button>
						))}
					</div>
					<div className="u-flex u-mta">
						<ButtonPrimary onClick={showPopup} disabled={!(tipAmount > 0)}>
							{tr.WE_SPIN_TIPPING_ROOM_TIP_BUTTON}
						</ButtonPrimary>
					</div>
				</>
			)}
			{!firstTipPage && (
				<HeadWrapper>
					<Icon type={ICON_HEART} />
					<MainTitle>{tr.WE_SPIN_TIPPING_ROOM_TIP_DONE_HEADER}</MainTitle>
					<SubTitle>
						{`${tr.WE_SPIN_TIPPING_ROOM_TIP_DONE_TEXT_PT1} ${streamerRef} ${tr.WE_SPIN_TIPPING_ROOM_TIP_DONE_TEXT_PT2}`}
					</SubTitle>
				</HeadWrapper>
			)}
		</div>
	);
};

export default WeSpinGameRoomTip;
